import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  output,
  signal,
} from '@angular/core';

import {
  ActionButton,
  ActionButtonBuilder,
  ActionButtonsComponent,
  DateTimeComponent,
  ToastService,
} from '@web/shared/ui';
import { AdminSubscriptionListAction } from '../../enums/admin-subscription-list-action';
import { of, switchMap, takeWhile, timer } from 'rxjs';
import { Nillable, valueOrDefault } from '@web/utils';
import { AdminSubscriptionDataSourceService } from '../../services';
import { AdminSubscriptionAfterActionExecuteEvent } from '../../events/admin-subscription-after-action-execute-event';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Route } from '@web/common';
import { AdminSubscriptionEventBusService } from '../../bus/services';
import { AdminSubscriptionPricesChangedBusEvent } from '../../bus/events';

@Component({
  selector: 'app-admin-subscription-list-actions',
  imports: [ActionButtonsComponent, DateTimeComponent],
  templateUrl: './admin-subscription-list-actions.component.html',
  styleUrl: './admin-subscription-list-actions.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminSubscriptionListActionsComponent implements OnInit {
  private readonly eventBus = inject(AdminSubscriptionEventBusService);

  /* prettier-ignore */
  public readonly afterActionExecuteEvent = output<AdminSubscriptionAfterActionExecuteEvent>();

  /* prettier-ignore */
  private readonly actionButtonBuilder = inject(ActionButtonBuilder<AdminSubscriptionListAction>);
  /* prettier-ignore */
  private readonly subscriptionDataSource = inject(AdminSubscriptionDataSourceService);

  private readonly visibleProcessSubscription = signal<boolean>(false);
  private readonly visiblePriceChangeSubscription = signal<boolean>(false);
  private readonly subscriptionToProcess = signal<number>(0);
  protected readonly nextScheduledProcessingDate = signal<Nillable<Date>>(null);
  private readonly toast = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly valueOrDefault = valueOrDefault;
  protected readonly actions = signal<
    ActionButton<AdminSubscriptionListAction>[]
  >([]);

  public constructor() {
    this.observeBusEvents();
  }

  public ngOnInit(): void {
    this.subscriptionDataSource
      .fetchSubscriptionsToProcessDetails()
      .subscribe((details) => {
        this.visibleProcessSubscription.set(details.isBulkProcessAvailable);
        this.subscriptionToProcess.set(details.itemsToProcess);
        this.nextScheduledProcessingDate.set(
          details.nextScheduledProcessingDate,
        );

        this.actions.set(this.buildActions());
      });

    this.subscriptionDataSource
      .fetchBulkPriceChangeDetails()
      .subscribe((details) => {
        this.visiblePriceChangeSubscription.set(
          details.isBulkPriceChangeAvailable,
        );

        this.actions.set(this.buildActions());
      });
  }

  /* prettier-ignore */
  private buildActions(): ActionButton<AdminSubscriptionListAction>[] {
    return [
      this.actionButtonBuilder
        .initCommand(AdminSubscriptionListAction.Process)
        .label(`Przetwarzaj subskrypcje (${this.subscriptionToProcess()})`)
        .visibility(() => of(this.visibleProcessSubscription()))
        .command(() => this.subscriptionDataSource.confirmAndProcessAvailable().subscribe(() => {
          this.visibleProcessSubscription.set(false);
          this.actions.set(this.buildActions());
          this.listenToProcessSubscriptions();
        }))
        .build(),

      this.actionButtonBuilder
        .initCommand(AdminSubscriptionListAction.ProcessPending)
        .label(`Przetwarzanie subskrypcji w tle`)
        .visibility(() => of(!this.visibleProcessSubscription()))
        .disability(() => true)
        .command(() => {
          // Do nothing
        })
        .build(),

      this.actionButtonBuilder
        .initRouterLink(AdminSubscriptionListAction.BulkPriceChange)
        .label('Zmień cenę')
        .visibility(() => of(this.visiblePriceChangeSubscription()))
        .routerLink((provider) => provider.routerLink(Route.SubscriptionBulkPriceChange))
        .build(),

      this.actionButtonBuilder
        .initCommand(AdminSubscriptionListAction.BulkPriceChangePending)
        .label(`Zmiana cen subskrypcji w tle`)
        .visibility(() => of(!this.visiblePriceChangeSubscription()))
        .disability(() => true)
        .command(() => {
          // Do nothing
        })
        .build(),
    ];
  }

  private notifyAfterActionExecuteEvent(
    action: AdminSubscriptionListAction,
  ): void {
    this.afterActionExecuteEvent.emit(
      new AdminSubscriptionAfterActionExecuteEvent({
        action,
      }),
    );
  }

  private observeBusEvents(): void {
    this.eventBus
      .observeMultiple([AdminSubscriptionPricesChangedBusEvent])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.visiblePriceChangeSubscription.set(false);
          this.actions.set(this.buildActions());

          this.listenToSubscriptionsPriceChange();
        },
      });
  }

  private listenToProcessSubscriptions(): void {
    const poolingTime = 10000;

    timer(0, poolingTime)
      .pipe(
        switchMap(() =>
          this.subscriptionDataSource.fetchSubscriptionsToProcessDetails(),
        ),
        takeWhile((details) => !details?.isBulkProcessAvailable, true),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (details) => {
          if (details.isBulkProcessAvailable) {
            this.toast.show((builder) =>
              builder
                .initSuccess('Zakończono przetwarzanie subskrypcji.')
                .build(),
            );

            this.visibleProcessSubscription.set(true);
            this.subscriptionToProcess.set(details.itemsToProcess);
            this.nextScheduledProcessingDate.set(
              details.nextScheduledProcessingDate,
            );
            this.actions.set(this.buildActions());
            this.notifyAfterActionExecuteEvent(
              AdminSubscriptionListAction.Process,
            );
          }
        },
      });
  }

  private listenToSubscriptionsPriceChange(): void {
    const poolingTime = 10000;

    timer(0, poolingTime)
      .pipe(
        switchMap(() =>
          this.subscriptionDataSource.fetchBulkPriceChangeDetails(),
        ),
        takeWhile((details) => !details?.isBulkPriceChangeAvailable, true),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (details) => {
          if (details.isBulkPriceChangeAvailable) {
            this.toast.show((builder) =>
              builder.initSuccess('Zakończono zmianę cen subskrypcji.').build(),
            );

            this.visiblePriceChangeSubscription.set(true);
            this.actions.set(this.buildActions());
            this.notifyAfterActionExecuteEvent(
              AdminSubscriptionListAction.BulkPriceChange,
            );
          }
        },
      });
  }
}
